import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useAuth } from "../hooks/useAuth";
import { mapDashboardStats, mapMonitorsResult } from "../api/profile";
import { MonitoringStatusCard } from "../components/MonitoringStatusCard";
import { EmptyState } from "../components/ui/EmptyState";
import { LoadingState } from "../components/ui/LoadingState";
import { ErrorState } from "../components/ui/ErrorState";
import { buildLoggedBillingPath, shouldShowLoggedRenewal } from "../lib/billingGuards";
import { t } from "../i18n";
import { Plus } from "lucide-react";

function InstagramIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
    </svg>
  );
}

export function AppDashboardPage() {
  const { session, entitlement } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const rawProfiles = useQuery(api.monitors.list, session ? {} : "skip");
  const rawStats = useQuery(api.stats.dashboardStats, session ? {} : "skip");
  const subscriptionStatus = useQuery(
    api.monitors.getUserSubscriptionStatus,
    session ? {} : "skip",
  );
  const removeMonitor = useMutation(api.monitors.remove);
  const profiles = useMemo(() => mapMonitorsResult(rawProfiles), [rawProfiles]);
  const stats = useMemo(() => mapDashboardStats(rawStats), [rawStats]);
  const isLoading = rawProfiles === undefined || rawStats === undefined || subscriptionStatus === undefined;
  const isEmptyLoaded = !isLoading && profiles.length === 0;
  const shouldRenew = shouldShowLoggedRenewal(subscriptionStatus);
  const planLimit = subscriptionStatus?.planLimit ?? entitlement?.planLimit ?? 0;

  const handleDelete = async (monitorId: string) => {
    setError(null);
    try {
      await removeMonitor({ monitorId: monitorId as Id<"userMonitors"> });
    } catch {
      setError(t("dashboardErrorLoad"));
    }
  };

  if (!session) {
    return (
      <EmptyState
        title={t("dashboardSignInTitle")}
        body={t("dashboardSignInBody")}
      />
    );
  }

  if (isLoading) {
    return <LoadingState title={t("commonLoading")} />;
  }

  return (
    <div className="app-page app-dashboard-page">
      <div className="app-page-header">
        <div>
          <h1>{t("appDashboard")}</h1>
          <p className="app-page-subtitle">
            {isEmptyLoaded ? t("dashboardEmptyHint") : t("dashboardOverviewSubtitle")}
          </p>
        </div>
        {planLimit > 0 && (
          <span className="app-limit-pill">
            {t("dashboardAccountLimit", {
              current: profiles.length,
              limit: planLimit,
            })}
          </span>
        )}
      </div>

      <button
        type="button"
        className="app-add-target-button"
        onClick={() => navigate(shouldRenew ? buildLoggedBillingPath({ reason: "expired" }) : "/app/add-target")}
        aria-label={t("dashboardAddProfile")}
      >
        <Plus size={34} />
        <span>{t("dashboardAddProfile")}</span>
      </button>

      {isEmptyLoaded && shouldRenew && (
        <section className="app-empty-panel app-dashboard-empty-card">
          <InstagramIcon />
          <h2>{t("renewalTitle")}</h2>
          <p>{t("renewalDescription")}</p>
          <button
            type="button"
            className="app-primary-button app-empty-action"
            onClick={() => navigate(buildLoggedBillingPath({ reason: "expired" }))}
          >
            {t("renewalCta")}
          </button>
        </section>
      )}

      {isEmptyLoaded && !shouldRenew && (
        <section className="app-empty-panel app-dashboard-empty-card">
          <InstagramIcon />
          <h2>{t("emptyProfilesTitle")}</h2>
          <p>{t("emptyProfilesBody")}</p>
          <div className="app-hero-chip-row">
            <span>{t("dashboardEmptyProof")}</span>
            <span>{t("dashboardFeatureNewFollows")}</span>
            <span>{t("dashboardFeatureUnfollows")}</span>
          </div>
        </section>
      )}

      {profiles.length > 0 && (
        <section className="app-summary-card">
          <div className="app-summary-chip">
            <span />
            {t("dashboardMonitoringChip")}
          </div>
          <div className="app-summary-numbers">
            <div>
              <strong>+{stats?.newFollowingCount ?? 0}</strong>
              <span>{t("timelineStartedFollowing")}</span>
            </div>
            <div>
              <strong>-{stats?.unfollowsCount ?? 0}</strong>
              <span>{t("timelineStoppedFollowing")}</span>
            </div>
          </div>
        </section>
      )}

      {error && <ErrorState message={error} />}

      {!error && profiles.length > 0 && (
        <div className="app-list">
          {profiles.map((profile) => (
            <MonitoringStatusCard
              key={profile.id}
              profile={profile}
              onClick={() => navigate(`/app/monitor-details/${profile.id}`)}
              onDelete={() => void handleDelete(profile.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
